import { getCookie, setCookie } from '@avalara/bat-apps-shared/helpers/cookies';

import {
  AVIATOR_CRUSH_LEAD_FIELD_NAMES,
  type AviatorCrushLead,
  isAviatorCrushLeadComplete,
} from './aviator-crush-lead-validation';
import { isProdEnv } from './helpers';

const COOKIE_NAME = 'aviatorCrushLead';
// Crush runs across a multi-day conference; keep the cookie alive for the whole event window so
// an attendee who drops their session and scans another QR code on a later day still gets
// recognized rather than re-submitting as a new lead.
const COOKIE_DURATION_MINUTES = 60 * 24 * 7;

export const setAviatorCrushLeadCookie = (lead: AviatorCrushLead): void => {
  setCookie({
    name: COOKIE_NAME,
    value: JSON.stringify(lead),
    isProduction: isProdEnv(),
    durationInMinutes: COOKIE_DURATION_MINUTES,
  });
};

const readRawLeadCookie = (): Partial<AviatorCrushLead> | null => {
  // getCookie already runs the whole `document.cookie` header through decodeURIComponent, so the
  // value it returns is the raw JSON string - no further decoding needed (see getAuiContextCookie,
  // which reads its own JSON cookie the same way).
  const raw = getCookie(COOKIE_NAME);
  if (raw === false) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    // Only keep the known fields, and only when they're actually strings - a tampered/malformed
    // cookie (e.g. `{"email":{},"firstName":[]}`) would otherwise reach LeadForm's validators,
    // which call string methods on these values and would throw during render.
    const candidate = parsed as Record<string, unknown>;
    const lead: Partial<AviatorCrushLead> = {};
    for (const fieldName of AVIATOR_CRUSH_LEAD_FIELD_NAMES) {
      const value = candidate[fieldName];
      if (typeof value === 'string') lead[fieldName] = value;
    }
    return lead;
  } catch {
    // Malformed cookie value (truncated, hand-edited, etc.) - treat as absent rather than throw.
    return null;
  }
};

export type AviatorCrushLeadCookieState =
  | { status: 'absent' }
  | { status: 'complete'; lead: AviatorCrushLead }
  | { status: 'partial'; lead: Partial<AviatorCrushLead> };

export const readAviatorCrushLeadCookie = (): AviatorCrushLeadCookieState => {
  const lead = readRawLeadCookie();
  if (!lead) return { status: 'absent' };

  return isAviatorCrushLeadComplete(lead) ? { status: 'complete', lead } : { status: 'partial', lead };
};
